import invariant from 'invariant';
import { batch } from 'react-redux';
import { Dispatch } from 'redux';

import { extractKeywordsFromQuery } from 'algolia-api/search/extractKeywordsFromQuery';
import { DynamicEndpoint } from 'algolia-api/types/DynamicEndpoint';
import { ListingType } from 'algolia-api/types/ListingType';
import { SearchFiltersWithType } from 'algolia-api/types/SearchFiltersWithType';
import { SearchLocation } from 'algolia-api/types/SearchLocation';
import { SalariesJobSynonym } from 'modules/salaries/types/SalariesJobSynonym';
import { trackEvent } from 'utils/analytics/track/trackEvent';
import { getDefaultSearchRadius } from 'utils/constants/general/defaultSearchRadius';
import { REMOTE_KEYWORDS } from 'utils/constants/search';
import { getJson } from 'utils/http/getJson';
import { setItem } from 'utils/localStorage';
import { MAIN_SEARCH_FACETS } from 'utils/mainSearchFacets';
import { camelCaseToSnakeCase } from 'utils/string';

import {
  GradSortType,
  MainSearchResults,
  MainStoreMainSearchState,
  SortType,
} from './MainStoreMainSearchState';
import { executeSearchQuery } from './helpers/executeSearchQuery';
import {
  ClearResultsAction,
  FetchPageSuccessAction,
  InitializeAction,
  MainStoreMainSearchAction,
  ResetFiltersAction,
  ResetPageAction,
  SuccessAction,
} from './mainSearch';
import { MainStoreSearchRadiusState } from './searchRadius';
import { setSearchRadius as setSearchRadiusBase } from './searchRadius.actions';

const EVERYWHERE_LOCATION = {
  full: null,
  text: null,
  geoType: null,
  stateCode: null,
  countryCode: null,
  latitude: null,
  longitude: null,
  boundingBoxN: null,
  boundingBoxS: null,
  boundingBoxW: null,
  boundingBoxE: null,
  hasGeo: false,
};

// Consts
const ENDPOINTS: DynamicEndpoint[] = [
  {
    facetUrl: (synonymId: string) =>
      `/data/salary-surveys/job-synonyms/${synonymId}`,
    facetKey: 'jobFamilyFacet',
    facetName: 'synonymId',
    filterKey: 'compdb.familyId',
    // TODO: Fix this the next time the file is edited.
    // @ts-expect-error fix types
    responseToOptions: ({
      familyId,
      jobSynonym,
    }: {
      familyId: string;
      jobSynonym: SalariesJobSynonym;
    }) => [{ text: jobSynonym.name, id: familyId }],
  },
];

// Utility operations on state

export function resultsFn(state: MainStoreMainSearchState) {
  return state.resultsByPage[state.pageIndex.toString()];
}

// Helpers

function extractLocationProperties(location: SearchLocation) {
  return {
    location_geo_type: location.geoType,
    location_latitude: location.latitude,
    location_longitude: location.longitude,
    location_state: location.stateCode,
    location_text: location.text,
  };
}

function extractFilters(filters: SearchFiltersWithType<ListingType>) {
  const result: Record<string, string | string[] | undefined> = {};
  Object.keys(filters).forEach((filterKey) => {
    let value = filters[filterKey];

    if (
      MAIN_SEARCH_FACETS[filterKey] &&
      value === MAIN_SEARCH_FACETS[filterKey].options
    ) {
      value = 'ALL';
    }

    result[`filter_${camelCaseToSnakeCase(filterKey)}`] = value;
  });
  return result;
}

export function getMainSearchTrackingData(
  {
    mainSearch: {
      initialized,
      filters,
      query,
      pageIndex,
      resultsByPage,
      searchLocation,
    },
    searchRadius: { value: radius },
  }: {
    mainSearch: MainStoreMainSearchState;
    searchRadius: MainStoreSearchRadiusState;
  },
  searchFrom?: string,
) {
  if (initialized && searchLocation) {
    const results = resultsByPage[pageIndex.toString()] || {
      nbHits: 0,
      nbPages: 0,
    };
    return {
      current_page: pageIndex + 1,
      geo_location_enabled: Boolean(searchLocation.latitude),
      ...extractFilters(filters),
      search_query: query,
      search_radius: radius,
      number_of_results: results.nbHits,
      number_of_pages: results.nbPages,
      search_from: searchFrom,
      ...extractLocationProperties(searchLocation),
    };
  }

  return {};
}

// Actions
export const updateSearchLocation =
  (location: SearchLocation, didUserChange: boolean) =>
  async (dispatch: Dispatch<MainStoreMainSearchAction>) => {
    if (didUserChange) {
      trackEvent(
        'Changed Search Location',
        extractLocationProperties(location),
      );
    }

    await Promise.resolve();
    setItem('searchLocation', location);
    return dispatch({
      type: 'UPDATE_SEARCH_LOCATION',
      location: { ...EVERYWHERE_LOCATION, ...location },
    });
  };

export const clearSearchLocation =
  (didUserChange: boolean) =>
  (dispatch: Dispatch<MainStoreMainSearchAction>) => {
    if (didUserChange) {
      trackEvent('Cleared Search Location');
    }

    return Promise.resolve()
      .then(() => setItem('searchLocation', EVERYWHERE_LOCATION))
      .then(() =>
        dispatch({
          type: 'CLEAR_SEARCH_LOCATION',
        }),
      );
  };

const searchResultsSuccessful = ({
  results,
}: {
  results: MainSearchResults;
}): SuccessAction => ({
  type: 'SEARCH_RESULTS_SUCCESS',
  results,
});

const fetchPageSuccessful = ({
  results,
}: {
  results: MainSearchResults;
}): FetchPageSuccessAction => ({
  type: 'SEARCH_FETCH_PAGE_SUCCESS',
  results,
});

const loadDynamicFacetOptions =
  (queryParams: Record<string, string>) =>
  (
    dispatch: Dispatch<MainStoreMainSearchAction>,
    getState: () => { mainSearch: MainStoreMainSearchState },
  ) => {
    const { dynamicFacets, facetsThatArentLoaded } = getState().mainSearch;
    return Promise.all(
      ENDPOINTS.map(({ facetUrl, facetName, facetKey, responseToOptions }) => {
        if (
          (facetsThatArentLoaded.length > 0 &&
            !facetsThatArentLoaded.includes(facetKey)) ||
          (facetsThatArentLoaded.length === 0 &&
            !Object.keys(dynamicFacets).includes(facetKey)) ||
          !queryParams[facetName]
        ) {
          dispatch({
            type: 'SET_SEARCH_FACET_OPTIONS',
            facetKey,
            options: [],
            optionTitles: {},
            optionRankings: {},
          });
          return Promise.resolve();
        }
        return getJson<{ familyId: string; jobSynonym: SalariesJobSynonym }>(
          typeof facetUrl === 'string'
            ? facetUrl
            : facetUrl(queryParams[facetName]),
        ).then((response) => {
          const options = responseToOptions(response);

          dispatch({
            type: 'SET_SEARCH_FACET_OPTIONS',
            facetKey,
            options: options.map((f) => f.id),
            optionTitles: options.reduce(
              (sum, { id, text }) => ({ ...sum, [id]: text }),
              {},
            ),
            optionRankings: options.reduce(
              (sum, { id, usageRanking }) => ({ ...sum, [id]: usageRanking }),
              {},
            ),
          });
        });
      }),
    );
  };

export const setQueryKeywords =
  (keywords: Array<string>) =>
  (dispatch: Dispatch<MainStoreMainSearchAction>) =>
    dispatch({
      type: 'SET_QUERY_KEYWORDS',
      keywords,
    });

export const setSearchFilter =
  (
    filterKey: string,
    value: string | string[] | Record<string, unknown> | null | undefined,
  ) =>
  (
    dispatch: Dispatch<MainStoreMainSearchAction>,
    getState: () => {
      mainSearch: MainStoreMainSearchState;
      searchRadius: MainStoreSearchRadiusState;
    },
  ) => {
    const selectedOptions = (state: { mainSearch: MainStoreMainSearchState }) =>
      state.mainSearch.filters[filterKey];

    if (filterKey === 'type') {
      invariant(value, 'type can never be set to undefined/null');
    }

    if (value === 'BLOG' && filterKey === 'type') return;

    dispatch({
      type: 'SEARCH_SET_FILTER',
      key: filterKey,
      value,
    });

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch<any>(setQueryKeywords([]));

    const state = getState();
    trackEvent('Changed Search Filter', {
      ...getMainSearchTrackingData(getState()),
      filter_name: filterKey,
      filter_value: selectedOptions(state),
      selected_value: value instanceof Array ? value[0] : value,
    });
  };

export const setSearchFilters =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (filters: any) => (dispatch: Dispatch<MainStoreMainSearchAction>) => {
    dispatch({
      type: 'SEARCH_SET_FILTERS',
      filters,
    });
  };

export const setSearchQuery =
  (query: string, didUserChange: boolean) =>
  (
    dispatch: Dispatch<MainStoreMainSearchAction>,
    getState: () => {
      mainSearch: MainStoreMainSearchState;
      searchRadius: MainStoreSearchRadiusState;
    },
  ) => {
    const { keywords: stateKeywords } = getState().mainSearch;

    if (didUserChange) {
      trackEvent('Changed Search Query', {
        ...getMainSearchTrackingData(getState()),
        new_query: query,
      });
    }

    const [keywords, parsedQuery] = extractKeywordsFromQuery(
      query,
      REMOTE_KEYWORDS[CURRENT_LOCALE],
    );

    if (Array.isArray(keywords) && keywords.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch<any>(setSearchFilter('locationTypeFacet', ['REMOTE']));
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch<any>(setQueryKeywords(keywords));
    } else if (Array.isArray(stateKeywords) && stateKeywords.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch<any>(setQueryKeywords([]));
    }

    dispatch({
      type: 'SEARCH_SET_QUERY',
      query: parsedQuery,
    });
  };

export const searchResetPage = (): ResetPageAction => ({
  type: 'SEARCH_RESET_PAGE',
});

export const setSearchRadius =
  (radius: string, didUserChange: boolean, preservePage?: boolean) =>
  (
    dispatch: Dispatch<MainStoreMainSearchAction>,
    getState: () => {
      mainSearch: MainStoreMainSearchState;
      searchRadius: MainStoreSearchRadiusState;
    },
  ) => {
    if (didUserChange) {
      trackEvent('Changed Search Radius', {
        ...getMainSearchTrackingData(getState()),
        radius,
      });
    }

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch<any>(setSearchRadiusBase(radius, didUserChange));
    if (!preservePage) {
      dispatch(searchResetPage());
    }
  };

export function initializeSearch(action: Omit<InitializeAction, 'type'>) {
  return (dispatch: Dispatch<MainStoreMainSearchAction>) => {
    const promises = [
      dispatch({
        type: 'SEARCH_INITIALIZE',
        ...action,
      }),
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch<any>(loadDynamicFacetOptions(action.queryParams)),
    ];

    if (action.queryParams.radius) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch<any>(setSearchRadius(action.queryParams.radius, false, true));
    }

    if (action.location) {
      promises.push(
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        dispatch<any>(updateSearchLocation(action.location, false)),
      );
    }

    return Promise.all(promises);
  };
}

export const uninitializeSearch =
  () => (dispatch: Dispatch<MainStoreMainSearchAction>) =>
    dispatch({
      type: 'SEARCH_UNINITIALIZE',
    });

export const setSearchSort =
  (sortParam: SortType | GradSortType) =>
  (
    dispatch: Dispatch<MainStoreMainSearchAction>,
    getState: () => {
      mainSearch: MainStoreMainSearchState;
      searchRadius: MainStoreSearchRadiusState;
    },
  ) => {
    const state = getState();
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line prefer-destructuring
    const searchRadius = state.searchRadius;
    trackEvent('Changed Search Sort', {
      ...getMainSearchTrackingData(state),
      sort_order: sortParam,
    });
    dispatch({
      type: 'SEARCH_SET_SORT',
      sortParam,
    });

    if (!searchRadius.userHasChanged) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch<any>(setSearchRadius(getDefaultSearchRadius(sortParam), false));
    }

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch<any>(setQueryKeywords([]));
  };
export const setPageIndex =
  (pageIndex: number, didUserChange: boolean) =>
  (
    dispatch: Dispatch<MainStoreMainSearchAction>,
    getState: () => {
      mainSearch: MainStoreMainSearchState;
      searchRadius: MainStoreSearchRadiusState;
    },
  ) => {
    if (didUserChange) {
      trackEvent('Changed Search Page Number', {
        ...getMainSearchTrackingData(getState()),
        new_page: pageIndex,
      });
    }

    dispatch({
      type: 'SEARCH_SET_PAGE_INDEX',
      pageIndex,
    });
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch<any>(setQueryKeywords([]));
  };

export const resetSearchFilters = (): ResetFiltersAction => ({
  type: 'SEARCH_RESET_FILTERS',
});

export const clearAllFilters =
  () =>
  (
    dispatch: Dispatch<MainStoreMainSearchAction>,
    getState: () => { mainSearch: MainStoreMainSearchState },
  ) => {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line prefer-destructuring
    const type = getState().mainSearch.filters.type;
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    const sort = getState().mainSearch.sort[type];
    batch(() => {
      dispatch(resetSearchFilters());
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch<any>(setSearchRadius(getDefaultSearchRadius(sort), false));
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch<any>(clearSearchLocation(true));
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch<any>(setQueryKeywords([]));
    });
  };

export const selectSearchFilterOption =
  (filterKey: string, option: string | null | undefined) =>
  (
    dispatch: Dispatch<MainStoreMainSearchAction>,
    getState: () => {
      mainSearch: MainStoreMainSearchState;
      searchRadius: MainStoreSearchRadiusState;
    },
  ) => {
    const selectedOptions = (state: { mainSearch: MainStoreMainSearchState }) =>
      state.mainSearch.filters[filterKey];

    dispatch({
      type: 'SEARCH_SELECT_FILTER_OPTION',
      key: filterKey,
      option,
    });

    const state = getState();
    trackEvent('Changed Search Filter', {
      ...getMainSearchTrackingData(state),
      filter_name: filterKey,
      filter_value: selectedOptions(state),
      selected_value: option,
    });
  };

export const deselectSearchFilterOption =
  (filterKey: string, option: string | null | undefined) =>
  (
    dispatch: Dispatch<MainStoreMainSearchAction>,
    getState: () => {
      mainSearch: MainStoreMainSearchState;
      searchRadius: MainStoreSearchRadiusState;
    },
  ) => {
    const selectedOptions = (state: { mainSearch: MainStoreMainSearchState }) =>
      state.mainSearch.filters[filterKey];

    if (filterKey === 'type') {
      invariant(option, 'type can never be set to undefined/null');
    }

    dispatch({
      type: 'SEARCH_DESELECT_FILTER_OPTION',
      key: filterKey,
      option,
    });
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch<any>(setQueryKeywords([]));

    const state = getState();
    trackEvent('Changed Search Filter', {
      ...getMainSearchTrackingData(state),
      filter_name: filterKey,
      filter_value: selectedOptions(state),
      deselected_value: option,
    });
  };

export const selectAllSearchFilterOptions =
  (filterKey: string) =>
  (
    dispatch: Dispatch<MainStoreMainSearchAction>,
    getState: () => {
      mainSearch: MainStoreMainSearchState;
      searchRadius: MainStoreSearchRadiusState;
    },
  ) => {
    const selectedOptions = (state: { mainSearch: MainStoreMainSearchState }) =>
      state.mainSearch.filters[filterKey];

    dispatch({
      type: 'SEARCH_SELECT_ALL_FILTER_OPTIONS',
      key: filterKey,
    });
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch<any>(setQueryKeywords([]));

    const state = getState();
    trackEvent('Changed Search Filter', {
      ...getMainSearchTrackingData(getState()),
      filter_name: filterKey,
      filter_value: selectedOptions(state),
      selected_value: 'ALL',
    });
  };

export const setUserInitiatedSearch = (searchMode = true) => ({
  type: 'SEARCH_INITIATED_BY_USER',
  searchMode,
});

export function mainStoreSearchFunction() {
  return (
    dispatch: Dispatch<MainStoreMainSearchAction>,
    getState: () => {
      mainSearch: MainStoreMainSearchState;
      searchRadius: MainStoreSearchRadiusState;
    },
  ) => {
    dispatch({
      type: 'SEARCH_STARTED',
    });
    const state = getState();
    const { filters, searchId, resultsForSearchId } = state.mainSearch;
    const newSearch = searchId !== resultsForSearchId;

    if (newSearch && filters.type !== 'ALL' && filters.type !== 'ORG') {
      return executeSearchQuery({
        state,
      }).then((results) => {
        dispatch(searchResultsSuccessful({ results }));
        return results;
      });
    }

    return executeSearchQuery({ state }).then((results) => {
      if (newSearch) {
        dispatch(searchResultsSuccessful({ results }));
      } else {
        dispatch(searchResultsSuccessful({ results }));
      }

      return results;
    });
  };
}

export const fetchPage =
  (pageIndex: number) =>
  (
    dispatch: Dispatch<MainStoreMainSearchAction>,
    getState: () => {
      mainSearch: MainStoreMainSearchState;
      searchRadius: MainStoreSearchRadiusState;
    },
  ) => {
    dispatch({
      type: 'SEARCH_STARTED',
    });
    executeSearchQuery({ state: getState(), pageIndex }).then((results) => {
      dispatch(fetchPageSuccessful({ results }));
    });
  };

export const clearResults = (): ClearResultsAction => ({
  type: 'SEARCH_CLEAR_RESULTS',
});
